import { useState, useMemo } from 'react';
import { useMetaData, useApi } from '../hooks/useApi';
import { useCategory } from '../context/CategoryContext';
import { useMonth } from '../context/MonthContext';
import { Loyalty } from '../utils/api';
import { StackedBar, ChartCard, ChartLoader, ChartError } from '../components/Charts';
import { fmtMonth } from '../utils/theme';
import { last6, topNplusOthers } from '../utils/brandShare';
import { isRideHailing } from '../utils/oneHr';

const SEGMENTS = ['Solus', 'Dual', 'Three+'];

export default function LoyaltyBrandShareView() {
  const { category }  = useCategory();
  const { months, sessionTypes, latestMonth, loading: metaLoading } = useMetaData();
  const { month }     = useMonth();
  const [sessionType, setSessionType] = useState('transactional_activity');
  const [segment,     setSegment]     = useState('Solus');

  const selectedMonth = month || latestMonth;

  // Ride Hailing & Taxi always uses 1-hour sessions (legacy table, through Apr 2026).
  const is1hr = isRideHailing(category);

  const { data, loading, error } = useApi(
    () => Loyalty.brandShare({ category, session_type: sessionType, window: is1hr ? '1hr' : undefined }),
    { category, sessionType, is1hr },
    !!category && !!months.length,
  );

  // 1hr legacy data ends at Apr 2026, so derive the 6-month window from the data.
  const months6 = useMemo(() => {
    if (is1hr) return [...new Set((data || []).map(r => r.month_id))].sort().slice(-6);
    return last6(months, selectedMonth);
  }, [is1hr, data, months, selectedMonth]);

  const series = useMemo(() => {
    if (!data) return [];
    const rows = data.filter(r => r.app_loyalty === segment);
    return topNplusOthers(rows, months6, { valueKey: 'total_15min_sessions' });
  }, [data, months6, segment]);

  if (metaLoading) return <div className="loading-state"><div className="spinner" /> Loading…</div>;

  return (
    <div>
      <div className="filters-bar">
        <span className="filter-label">Session</span>
        <select className="filter-select" value={sessionType} onChange={e => setSessionType(e.target.value)}>
          {sessionTypes.map(s => <option key={s} value={s}>{s}</option>)}
        </select>

        <span className="filter-label">Loyalty</span>
        <select className="filter-select" value={segment} onChange={e => setSegment(e.target.value)}>
          {SEGMENTS.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>

      <ChartCard
        title={`Brand Share of Sessions — ${segment} users${is1hr ? ' (1-hour)' : ''}`}
        sub={`${category} · ${sessionType} · ${is1hr ? '1-hour sessions, through Apr 2026' : 'last 6 months'}`}
      >
        {loading  && <ChartLoader height={360} />}
        {error    && <ChartError message={error} height={360} />}
        {!loading && !error && series.length > 0 && (
          <StackedBar categories={months6.map(fmtMonth)} series={series} height={360} normalize />
        )}
        {!loading && !error && series.length === 0 && (
          <div className="empty-state">No {segment} session data for this category.</div>
        )}
      </ChartCard>
    </div>
  );
}
